import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from './cn';

type ButtonColor = 'red' | 'yellow' | 'green' | 'orange' | 'blue' | 'purple';

const gameplayColors = {
  red: [
    'bg-coral text-white shadow-[2px_3px_0px_0px_var(--coral-600),2px_3px_0px_2px_var(--black)]',
    'with-hover:hover:border-coral-800 with-hover:hover:bg-coral-300 with-hover:hover:text-coral-800 with-hover:hover:shadow-[2px_3px_0px_0px_var(--coral-600),2px_3px_0px_2px_var(--black)]',
    'active:translate-y-[3px] with-hover:active:text-coral-600 active:translate-x-[2px] with-hover:active:border-2 with-hover:active:text-coral-600 with-hover:active:bg-coral-100 with-hover:active:shadow-none',
  ].join(' '),
  yellow: [
    'bg-yellow text-black shadow-[2px_3px_0px_0px_var(--yellow-600),2px_3px_0px_2px_var(--black)]',
    'with-hover:hover:border-yellow-800 with-hover:hover:bg-yellow-300 with-hover:hover:text-yellow-800 with-hover:hover:shadow-[2px_3px_0px_0px_var(--yellow-600),2px_3px_0px_2px_var(--black)]',
    'active:translate-y-[3px] with-hover:active:text-yellow-600 active:translate-x-[2px] with-hover:active:border-2 with-hover:active:text-yellow-600 with-hover:active:bg-yellow-100 with-hover:active:shadow-none',
  ].join(' '),
  green: [
    'bg-green text-white shadow-[2px_3px_0px_0px_var(--green-600),2px_3px_0px_2px_var(--black)]',
    'with-hover:hover:border-green-800 with-hover:hover:bg-green-300 with-hover:hover:text-green-800 with-hover:hover:shadow-[2px_3px_0px_0px_var(--green-600),2px_3px_0px_2px_var(--black)]',
    'active:translate-y-[3px] with-hover:active:text-green-600 active:translate-x-[2px] with-hover:active:border-2 with-hover:active:text-green-600 with-hover:active:bg-green-100 with-hover:active:shadow-none',
  ].join(' '),
  orange: [
    'bg-orange text-black shadow-[2px_3px_0px_0px_var(--orange-700),2px_3px_0px_2px_var(--black)]',
    'with-hover:hover:border-orange-800 with-hover:hover:bg-orange-300 with-hover:hover:text-orange-800 with-hover:hover:shadow-[2px_3px_0px_0px_var(--orange-700),2px_3px_0px_2px_var(--black)]',
    'active:translate-y-[3px] with-hover:active:text-orange-600 active:translate-x-[2px] with-hover:active:border-2 with-hover:active:text-orange-600 with-hover:active:bg-orange-100 with-hover:active:shadow-none',
  ].join(' '),
  blue: [
    'bg-blue text-black shadow-[2px_3px_0px_0px_var(--blue-600),2px_3px_0px_2px_var(--black)]',
    'with-hover:hover:border-blue-800 with-hover:hover:bg-blue-300 with-hover:hover:text-blue-800 with-hover:hover:shadow-[2px_3px_0px_0px_var(--blue-600),2px_3px_0px_2px_var(--black)]',
    'active:translate-y-[3px] with-hover:active:text-blue-600 active:translate-x-[2px] with-hover:active:border-2 with-hover:active:text-blue-600 with-hover:active:bg-blue-100 with-hover:active:shadow-none',
  ].join(' '),
  purple: [
    'bg-purple text-white shadow-[2px_3px_0px_0px_var(--purple-600),2px_3px_0px_2px_var(--black)]',
    'with-hover:hover:border-purple-800 with-hover:hover:bg-purple-300 with-hover:hover:text-purple-800 with-hover:hover:shadow-[2px_3px_0px_0px_var(--purple-600),2px_3px_0px_2px_var(--black)]',
    'active:translate-y-[3px] with-hover:active:text-purple-600 active:translate-x-[2px] with-hover:active:border-2 with-hover:active:text-purple-600 with-hover:active:bg-purple-100 with-hover:active:shadow-none',
  ].join(' '),
};

const ledgeColors = {
  default: 'bg-white text-black with-hover:hover:bg-yellow-t2',
  red: 'bg-coral text-white with-hover:hover:bg-coral-600',
  yellow: 'bg-yellow text-black with-hover:hover:bg-yellow-300',
  green: 'bg-green text-white with-hover:hover:bg-green-600',
  orange: 'bg-orange text-black with-hover:hover:bg-orange-300',
  blue: 'bg-blue text-black with-hover:hover:bg-blue-300',
  purple: 'bg-purple text-white with-hover:hover:bg-purple-600',
};

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-bold focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none  [&_svg]:shrink-0 [&_svg]:fill-current box-border',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs with-hover:hover:bg-primary/90',
        none: '',
        gameplay: [
          'border-2 border-black rounded-[5px] bg-white shadow-[2px_3px_0px_0px_var(--gray-200),2px_3px_0px_2px_var(--black)]',
          'with-hover:hover:border-black with-hover:hover:bg-yellow-t2 with-hover:hover:text-yellow-s1 with-hover:hover:shadow-[2px_3px_0px_0px_var(--yellow-600),2px_3px_0px_2px_var(--black)]',
          'active:translate-y-[3px] active:translate-x-[2px] active:border-2  active:shadow-none',
          'disabled:border-gray-3 disabled:bg-white disabled:text-gray-3 disabled:shadow-[2px_3px_0px_0px_var(--gray-200),2px_3px_0px_2px_var(--gray-300)]',
          'data-[pressed=true]:text-yellow-s2 data-[pressed=true]:border-yellow-s2 data-[pressed=true]:bg-yellow-t1 data-[pressed=true]:shadow-[2px_3px_0px_0px_var(--yellow-600),2px_3px_0px_2px_var(--black)]',
        ].join(' '),
        pencil: [
          'border-2 border-black rounded-[5px] bg-white shadow-[2px_3px_0px_0px_var(--gray-200),2px_3px_0px_2px_var(--black)]',
          'with-hover:hover:border-purple-800 with-hover:hover:bg-purple-t2 with-hover:hover:text-purple-s1 with-hover:hover:shadow-[2px_3px_0px_0px_var(--purple-600),2px_3px_0px_2px_var(--black)]',
          'active:translate-y-[3px] active:translate-x-[2px] active:border-2 active:shadow-none',
          'disabled:border-gray-3 disabled:bg-white disabled:text-gray-3 disabled:shadow-[2px_3px_0px_0px_var(--gray-200),2px_3px_0px_2px_var(--gray-300)]',
          'data-[pressed=true]:bg-purple-300 data-[pressed=true]:border-purple-800 data-[pressed=true]:text-purple-800 data-[pressed=true]:shadow-[2px_3px_0px_0px_var(--purple-600),2px_3px_0px_2px_var(--black)]',
        ].join(' '),
        flat: [
          'border-2 border-transparent with-hover:hover:border-black with-hover:hover:rounded-[50px] with-hover:hover:bg-yellow-t2',
          'active:border-2 active:border-yellow-s2 active:rounded-[50px] active:bg-yellow-t1',
          'disabled:border-2 disabled:border-gray-1 disabled:rounded-[50px] disabled:bg-white',
          'data-[pressed=true]:border-2 data-[pressed=true]:border-yellow-s2 data-[pressed=true]:rounded-[50px] data-[pressed=true]:bg-yellow-t1',
        ].join(' '),
        // A flat-faced button standing on a black ledge, which it drops onto when pressed. The ledge is a
        // shadow rather than a thicker bottom border, so the press doesn't change the layout height. Its
        // fills come from `ledgeColors`, white by default.
        ledge:
          'border-2 border-black rounded-lg shadow-[0_2px_0_0_var(--black)] active:translate-y-[2px] active:shadow-none',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        // The page-action size: the solved screen and the results page built this from `lg` plus
        // overrides, in enough places that it was really a size of its own.
        page: 'h-9 rounded-md px-5',
        // The home page's game cards: `page`, a step taller, with a touch more type and narrower sides.
        card: 'h-10 rounded-md px-3 text-[15px]',
        icon: 'h-9 w-9',
        gameplay: 'py-3 px-[11px] md:px-[13px] rounded-[10px] text-lg md:text-[19px]',
        'gameplay-sm': 'p-2 rounded-[8px] text-lg md:py-3 md:px-[11px] md:rounded-[10px] md:text-[19px]',
        flat: 'px-3 py-2  text-base',
        // With the 2px ledge beneath, the `ledge` variant stands 36px tall.
        ledge: 'h-[34px] px-3',
      },
      color: { default: '', red: '', yellow: '', green: '', orange: '', blue: '', purple: '' },
    },
    compoundVariants: [
      ...Object.entries(gameplayColors).map(([color, className]) => ({
        variant: 'gameplay' as const,
        color: color as ButtonColor,
        className,
      })),
      ...Object.entries(ledgeColors).map(([color, className]) => ({
        variant: 'ledge' as const,
        color: color as keyof typeof ledgeColors,
        className,
      })),
    ],
    defaultVariants: {
      variant: 'gameplay',
      size: 'gameplay',
      color: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  pressed?: boolean;
  color?: 'red' | 'yellow' | 'green' | 'orange' | 'blue' | 'purple';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, color, asChild = false, pressed, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, color, className }))}
        ref={ref}
        data-pressed={pressed}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
